import React, { Fragment, PureComponent, ReactNode, useState } from 'react';
import Fuse from 'fuse.js';
import { connect } from 'react-redux';
import Hotkeys from 'react-hot-keys';
import { getTemplatesMeta, TemplateMeta } from '../../../../store/idbHandler';
import { ThunkDispatch } from 'redux-thunk';
import { GlobalState } from '../../../../store/reducers';
import { Actions } from '../../../../store/actions';
import {
    applyPrescriptionTemplate,
    deletePrescriptionTemplate,
    PX_TEMPLATE_ENTITY,
} from '../../../../store/actions/prescriptionPad';
import { DB_TABLES } from '../../../../store/dbTables';
import {
    INVESTIGATION,
    MEDICINES,
    DIAGNOSIS,
    SYMPTOMS,
    LAB_VITALS,
    EXAMINATIONS,
    INJECTIONS,
    PROCEDURES,
} from '../../../../strings';
import Modal from '../../../common/Modal';
import WithBodyClickClose from '../../../common/WithBodyClickClose';
import PxToolKeyOptions from './padV2/PxToolKeyOptions';
import { ALL_KINDS } from '../../../../utils/enums';
import { getMyDbId } from '../../../../api/mactiveid';
import notification from 'antd/lib/notification';

export enum KindToPxtoolKeys {
    LAB_TESTS_KIND = 'labTests',
    MEDICINES_KIND = 'medications',
    DIAGNOSIS_KIND = 'diagnosis',
    SYMPTOMS_KIND = 'symptoms',
    LAB_VITALS_KIND = 'labVitals',
    EXAMINATIONS_KIND = 'examinations',
    INJECTIONS_KIND = 'injections',
    PX = 'PX',
    PROCEDURES_KIND = 'procedures',
}

export const getGenieTable = (kind: ALL_KINDS): DB_TABLES | undefined => {
    let table: DB_TABLES | undefined;
    switch (kind) {
        case 'PX':
            table = DB_TABLES['prescriptionTemplates'];
            break;
        case 'LAB_TESTS_KIND':
            table = DB_TABLES['gLTTemplates'];
            break;
        case 'MEDICINES_KIND':
            table = DB_TABLES['gMedTemplates'];
            break;
        case 'INJECTIONS_KIND':
            table = DB_TABLES['gInjectionTemplates'];
            break;
        case 'DIAGNOSIS_KIND':
            table = DB_TABLES['gDiaTemplates'];
            break;
        case 'SYMPTOMS_KIND':
            table = DB_TABLES['gSymTemplates'];
            break;
        case 'LAB_VITALS_KIND':
            table = DB_TABLES['gLabVitalsTemplates'];
            break;
        case 'EXAMINATIONS_KIND':
            table = DB_TABLES['gExaminationsTemplates'];
            break;
        case 'PROCEDURES_KIND':
            table = DB_TABLES['gProceduresTemplates'];
            break;
    }
    return table;
};

const fuseConfig = { keys: ['name', 'subString'], threshold: 0.3, ignoreLocation: true };

type State = {
    list: TemplateMeta[];
    search: string;
};

type Props = {
    dispatch: ThunkDispatch<GlobalState, null, Actions>;
    close: () => void;
    isV2: boolean;
    kind: ALL_KINDS;
    isModal: boolean;
    withPeak: boolean;
    isForPackages?: boolean;
};

export const TITLE_MAP: { [key in keyof typeof ALL_KINDS]: string } = {
    PX: 'Prescription Templates',
    LAB_TESTS_KIND: `${INVESTIGATION} (Panel)`,
    MEDICINES_KIND: `${MEDICINES}`,
    DIAGNOSIS_KIND: `${DIAGNOSIS}`,
    SYMPTOMS_KIND: `${SYMPTOMS}`,
    LAB_VITALS_KIND: `${LAB_VITALS}`,
    EXAMINATIONS_KIND: `${EXAMINATIONS}`,
    INJECTIONS_KIND: `${INJECTIONS}`,
    PROCEDURES_KIND: `${PROCEDURES}`,
};

class UseTemplateDialog extends PureComponent<Props, State> {
    state: State = {
        list: [],
        search: '',
    };

    fuse = new Fuse([] as TemplateMeta[], fuseConfig);

    getTemplatesData = () => {
        const table: DB_TABLES | undefined = getGenieTable(this.props.kind);
        if (!table) return;

        getTemplatesMeta(getMyDbId() || '', table).then((list) => {
            this.setState({
                list,
            });
            this.fuse = new Fuse(list, fuseConfig);
        });
    };

    componentDidMount = (): void => {
        this.getTemplatesData();
    };

    onDeleteTemplate = (id: string) => {
        this.props.dispatch(deletePrescriptionTemplate(KindToPxtoolKeys[this.props.kind], id));
        this.getTemplatesData();
    };

    onSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        this.setState({
            search: e.target.value,
        });
    };

    MAP: { [k in keyof typeof ALL_KINDS]: PX_TEMPLATE_ENTITY } = {
        PX: 'PX',
        LAB_TESTS_KIND: 'labTests',
        MEDICINES_KIND: 'medications',
        DIAGNOSIS_KIND: 'diagnosis',
        SYMPTOMS_KIND: 'symptoms',
        LAB_VITALS_KIND: 'labVitals',
        EXAMINATIONS_KIND: 'examinations',
        INJECTIONS_KIND: 'injections',
        PROCEDURES_KIND: 'procedures',
    };

    applyTemplate = ({ id }: { id: string }) => {
        this.props.dispatch(applyPrescriptionTemplate(this.MAP[this.props.kind], id));
        notification.success({
            message: `Copied to Pad`,
            duration: 5,
            placement: 'bottomRight',
        });
        this.props.close();
    };

    title = `Search ${TITLE_MAP[this.props.kind]} `;

    render(): ReactNode {
        const { list, search } = this.state;
        const { close, isV2, isModal, withPeak, isForPackages } = this.props;

        const filteredList = search ? this.fuse.search(search).map((option) => option.item) : list;

        const Wrapper = isModal ? Modal : WithBodyClickClose;
        const showTemplateCss = withPeak
            ? `absolute w-320 lg:w-368 z-50 top-40 -right-30 rounded-16 pt-16 global-template-popup popup space-y-16 flex flex-col border border-neutral-7  max-h-${
                  isForPackages ? 360 : 296
              } lg:max-h-${isForPackages ? 360 : 296}`
            : 'z-1 rounded-16 py-16 space-y-16 flex flex-col bg-white h-full w-full';

        return isV2 ? (
            <Wrapper closeMe={close} contentCssClasses={'h-2/3'}>
                <TemplateDialogV2
                    title={this.title}
                    filteredList={filteredList}
                    kind={this.props.kind}
                    search={search}
                    onSearchChange={this.onSearchChange}
                    closeMe={close}
                    applyTemplate={this.applyTemplate}
                    onDeleteTemplate={this.onDeleteTemplate}
                    showTemplateCss={showTemplateCss}
                    isForPackages={isForPackages}
                />
            </Wrapper>
        ) : (
            <TemplateDialogV1
                title={this.title}
                filteredList={filteredList}
                kind={this.props.kind}
                search={search}
                onSearchChange={this.onSearchChange}
                closeMe={close}
                applyTemplate={this.applyTemplate}
                onDeleteTemplate={this.onDeleteTemplate}
                showTemplateCss={showTemplateCss}
            />
        );
    }
}

type UiProps = {
    title?: string;
    search: string;
    onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    kind: ALL_KINDS;
    closeMe: () => void;
    applyTemplate: ({ id }: { id: string }) => void;
    onDeleteTemplate: (id: string) => void;
    filteredList: TemplateMeta[];
    isModal?: boolean;
    showTemplateCss: string;
    isForPackages?: boolean;
};

type UiListItemProps = {
    t: TemplateMeta;
    kind: ALL_KINDS;
    applyTemplate: ({ id }: { id: string }) => void;
    onDeleteTemplate: (id: string) => void;
};

const TemplateDialogV1: React.FC<UiProps> = ({
    closeMe,
    search,
    onSearchChange,
    applyTemplate,
    filteredList,
}) => {
    return (
        <Fragment>
            <div className="flex items-center justify-between">
                <p className="text-neutral-black font-700">Use Templates</p>
                <div onClick={closeMe} className="flex items-center justify-center w-24 h-24">
                    <svg
                        width="20"
                        height="20"
                        viewBox="0 0 320 512"
                        className="cursor-pointer fill-neutral-5"
                    >
                        <path d="M193.94 256L296.5 153.44l21.15-21.15c3.12-3.12 3.12-8.19 0-11.31l-22.63-22.63c-3.12-3.12-8.19-3.12-11.31 0L160 222.06 36.29 98.34c-3.12-3.12-8.19-3.12-11.31 0L2.34 120.97c-3.12 3.12-3.12 8.19 0 11.31L126.06 256 2.34 379.71c-3.12 3.12-3.12 8.19 0 11.31l22.63 22.63c3.12 3.12 8.19 3.12 11.31 0L160 289.94 262.56 392.5l21.15 21.15c3.12 3.12 8.19 3.12 11.31 0l22.63-22.63c3.12-3.12 3.12-8.19 0-11.31L193.94 256z"></path>
                    </svg>
                </div>
            </div>
            <div className="mt-8">
                <div className="bg-white border border-neutral-3 flex flex-col rounded-4 focus:outline-none max-w-sm w-full">
                    <div className="flex items-center justify-between w-full bg-transparent">
                        <input
                            type="search"
                            className="search-input text-14 px-16 py-8"
                            name="ether-search-component"
                            autoComplete="off"
                            placeholder="Search templates"
                            value={search}
                            onChange={onSearchChange}
                        />
                        <div className="mr-12">
                            <svg
                                width="25"
                                height="25"
                                viewBox="0 0 28 28"
                                className="fill-neutral-5 cursor-pointer"
                                preserveAspectRatio="xMidYMid meet"
                            >
                                <path d="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z"></path>
                            </svg>
                        </div>
                    </div>
                    <div className="px-16 opacity-60 text-11"></div>
                </div>
            </div>
            <div className="flex flex-wrap items-center mt-8">
                {filteredList.length === 0 && <p>No template found</p>}
                {filteredList.map((l) => (
                    <span
                        onClick={() => applyTemplate(l)}
                        key={l.id}
                        className="mt-12 mr-12 text-accent-2 bg-accent-2-10 text-14 px-8 py-2 rounded-4 inline-flex items-center cursor-pointer"
                    >
                        {l.name}
                    </span>
                ))}
            </div>
        </Fragment>
    );
};

const TemplateDialogV2ListItem: React.FC<UiListItemProps> = ({
    t,
    kind,
    applyTemplate,
    onDeleteTemplate,
}) => {
    return (
        <div
            key={t.id}
            className="template-list px-24 pb-16 flex items-start space-x-12 cursor-pointer"
        >
            <div
                className={`flex  rounded-full max-w-27 max-h-27 min-w-27 min-h-27 ${
                    PxToolKeyOptions[KindToPxtoolKeys[kind]].showTemplateBackgroundColor
                } items-center justify-center w-24 h-24 relative`}
            >
                <svg
                    className={`w-16 h-16 ${
                        PxToolKeyOptions[KindToPxtoolKeys[kind]].showTemplateIconFillColor
                    }`}
                    role="img"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 448 512"
                >
                    <path d="M448 56v80C448 149.3 437.3 160 424 160S400 149.3 400 136V80h-152v352h48c13.25 0 24 10.75 24 24S309.3 480 296 480h-144C138.8 480 128 469.3 128 456s10.75-24 24-24h48v-352H48v56C48 149.3 37.25 160 24 160S0 149.3 0 136v-80C0 42.75 10.75 32 24 32h400C437.3 32 448 42.75 448 56z"></path>
                </svg>
                <svg
                    className={`absolute -right-1 bottom-0 w-10 h-10 ${
                        PxToolKeyOptions[KindToPxtoolKeys[kind]].showTemplateIconFillColor
                    }`}
                    role="img"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 448 512"
                >
                    <path d="M224 256c70.7 0 128-57.31 128-128s-57.3-128-128-128C153.3 0 96 57.31 96 128S153.3 256 224 256zM274.7 304H173.3C77.61 304 0 381.6 0 477.3c0 19.14 15.52 34.67 34.66 34.67h378.7C432.5 512 448 496.5 448 477.3C448 381.6 370.4 304 274.7 304z"></path>
                </svg>
            </div>
            <div className="flex items-center w-full justify-between">
                <div
                    className="flex-1"
                    onClick={(e) => {
                        applyTemplate(t);
                        e.stopPropagation();
                    }}
                >
                    <p className="text-neutral-black text-left font-700 text-14">{t.name}</p>
                    {t.subString && (
                        <p className="text-darwin-neutral-700 text-left text-12 truncate w-192 lg:w-240">
                            {t.subString}
                        </p>
                    )}
                </div>
                <div
                    className="delete-icon"
                    onClick={() => {
                        onDeleteTemplate(t.id);
                    }}
                >
                    <svg
                        viewBox="64 64 896 896"
                        focusable="false"
                        data-icon="delete"
                        width="1.1em"
                        height="1.1em"
                        fill="rgb(97, 106, 143)"
                        aria-hidden="true"
                    >
                        <path d="M360 184h-8c4.4 0 8-3.6 8-8v8h304v-8c0 4.4 3.6 8 8 8h-8v72h72v-80c0-35.3-28.7-64-64-64H352c-35.3 0-64 28.7-64 64v80h72v-72zm504 72H160c-17.7 0-32 14.3-32 32v32c0 4.4 3.6 8 8 8h60.4l24.7 523c1.6 34.1 29.8 61 63.9 61h454c34.2 0 62.3-26.8 63.9-61l24.7-523H888c4.4 0 8-3.6 8-8v-32c0-17.7-14.3-32-32-32zM731.3 840H292.7l-24.2-512h487l-24.2 512z"></path>
                    </svg>
                </div>
            </div>
        </div>
    );
};

const TemplateDialogV2: React.FC<UiProps> = ({
    title,
    closeMe,
    search,
    kind,
    onSearchChange,
    applyTemplate,
    onDeleteTemplate,
    filteredList,
    showTemplateCss,
    isForPackages,
}) => {
    const [showPackages, setShowPackages] = useState(false);
    const list = showPackages
        ? filteredList.filter((i) => i.isPackage)
        : filteredList.filter((i) => !i.isPackage);

    return (
        <Hotkeys keyName="esc" onKeyDown={closeMe}>
            <div id={'dialog'} className={showTemplateCss}>
                <div className="px-16 flex items-center justify-between ">
                    {title && <p className="text-darwin-neutral-1000 font-700 text-13">{title}</p>}
                    <div
                        onClick={(e) => {
                            closeMe();
                            e.stopPropagation();
                        }}
                        className="flex items-center space-x-4 cursor-pointer"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 320 512"
                            className="w-16 h-16 fill-neutral-3 hover:fill-neutral-1"
                        >
                            <path d="M193.94 256L296.5 153.44l21.15-21.15c3.12-3.12 3.12-8.19 0-11.31l-22.63-22.63c-3.12-3.12-8.19-3.12-11.31 0L160 222.06 36.29 98.34c-3.12-3.12-8.19-3.12-11.31 0L2.34 120.97c-3.12 3.12-3.12 8.19 0 11.31L126.06 256 2.34 379.71c-3.12 3.12-3.12 8.19 0 11.31l22.63 22.63c3.12 3.12 8.19 3.12 11.31 0L160 289.94 262.56 392.5l21.15 21.15c3.12 3.12 8.19 3.12 11.31 0l22.63-22.63c3.12-3.12 3.12-8.19 0-11.31L193.94 256z"></path>
                        </svg>
                    </div>
                </div>

                {isForPackages && (
                    <div className="grid grid-cols-2 px-16">
                        <button
                            className={`pb-4 font-700 
                            ${
                                !showPackages
                                    ? 'text-darwin-primary-500 border-b-2 border-darwin-primary-500'
                                    : ''
                            }
                            `}
                            onClick={() => setShowPackages(false)}
                        >
                            Templates
                        </button>
                        <button
                            className={`pb-4 font-700 ${
                                showPackages
                                    ? 'text-darwin-primary-500 border-b-2 border-darwin-primary-500'
                                    : ''
                            }`}
                            onClick={() => setShowPackages(true)}
                        >
                            Packages
                        </button>
                    </div>
                )}

                <div className="px-16">
                    <div className="px-12 w-full rounded-8 border border-neutral-7 flex items-center space-x-10 focus-within:bg-neutral-9 bg-white">
                        <div className="flex items-center space-x-12">
                            <svg
                                className="w-16 h-16 fill-neutral-black"
                                role="img"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 512 512"
                            >
                                <path d="M504.1 471l-134-134C399.1 301.5 415.1 256.8 415.1 208c0-114.9-93.13-208-208-208S-.0002 93.13-.0002 208S93.12 416 207.1 416c48.79 0 93.55-16.91 129-45.04l134 134C475.7 509.7 481.9 512 488 512s12.28-2.344 16.97-7.031C514.3 495.6 514.3 480.4 504.1 471zM48 208c0-88.22 71.78-160 160-160s160 71.78 160 160s-71.78 160-160 160S48 296.2 48 208z"></path>
                            </svg>
                        </div>
                        <input
                            type="text"
                            autoFocus
                            className="w-full text-14 placeholder-darwin-neutral-500 text-neutral-black bg-transparent py-10"
                            placeholder={`Start typing ${
                                showPackages ? 'package' : 'template'
                            } name or elements`}
                            value={search}
                            onChange={onSearchChange}
                        />
                    </div>
                </div>
                <div
                    className="flex-1 flex-auto overflow-auto w-full pt-4"
                    style={{
                        height: 'calc(100% - 94px);',
                    }}
                >
                    {list.length > 0 ? (
                        list.map((t) => (
                            <TemplateDialogV2ListItem
                                t={t}
                                kind={kind}
                                applyTemplate={applyTemplate}
                                onDeleteTemplate={onDeleteTemplate}
                            />
                        ))
                    ) : (
                        <div className="mb-16 text-left">
                            <span className="px-20">
                                No {showPackages ? 'packages' : 'templates'} found
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </Hotkeys>
    );
};

export default connect()(UseTemplateDialog);
